import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    inject,
    OnInit,
    signal,
    WritableSignal,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Map as MapLibreMap } from 'maplibre-gl';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { ImmersiveViewService } from '@connect-services/client/services/immersive-view/immersive-view.service';
import { LegendService } from '@connect-services/client/services/legend.service';
import { MapToImageService } from '@connect-services/client/services/map-to-image.service';
import { CheckboxComponent, CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

export enum MapExportFormatEnum {
    JPEG = 'jpeg',
    PNG = 'png',
    SVG = 'svg',
}

export enum MapExportPrintSizeEnum {
    A0 = 'a0',
    A1 = 'a1',
    A2 = 'a2',
    A3 = 'a3',
    A4 = 'a4',
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CheckboxComponent,
        ComplexModalTemplate,
        InputTextComponent,
        LoadingComponent,
        RessourcesModule,
        SelectComponent,
    ],
    selector: 'app-map-export-modal',
    templateUrl: './map-export-modal.component.html',
})
export class MapExportModalComponent implements AfterViewInit, OnInit {
    protected readonly addLegend = signal(true);
    protected readonly addNorthStar = signal(true);
    protected readonly addScale = signal(true);
    protected readonly CheckboxState = CheckboxState;
    protected readonly data: { map: MapLibreMap } = inject(MAT_DIALOG_DATA);
    protected readonly defaultPrintSizeOption: SelectOption<MapExportPrintSizeEnum>[] = [
        { label: MapExportPrintSizeEnum.A4.toUpperCase(), value: MapExportPrintSizeEnum.A4 },
    ];
    protected readonly fileTitle: WritableSignal<FormControl<string | null>> = signal(
        new FormControl(null, { nonNullable: true }),
    );
    protected formatOptions: SelectOption<MapExportFormatEnum>[] = Object.values(MapExportFormatEnum).map(
        (value: MapExportFormatEnum) => ({
            label: value.toUpperCase(),
            value: value,
        }),
    );
    protected readonly isMapImageLoading = signal(false);
    protected readonly MapExportFormatEnum = MapExportFormatEnum;
    protected readonly mapToImageService = inject(MapToImageService);
    protected printSizeOptions: SelectOption<MapExportPrintSizeEnum>[] = Object.values(MapExportPrintSizeEnum).map(
        (value: MapExportPrintSizeEnum) => ({
            label: value.toUpperCase(),
            value: value,
        }),
    );
    protected readonly selectedFormat = signal<MapExportFormatEnum>(MapExportFormatEnum.JPEG);

    private readonly bearing = signal(0);
    private readonly dialogRef = inject(MatDialogRef<MapExportModalComponent>);
    private readonly immersiveViewService = inject(ImmersiveViewService);
    private readonly legendService = inject(LegendService);
    private readonly selectedPrintSize = signal<MapExportPrintSizeEnum>(MapExportPrintSizeEnum.A4);

    public async ngAfterViewInit(): Promise<void> {
        await this.mapToImageService.generateImageAndDisplay(this.data.map, {
            hideAllControls: true,
            hideMarkers: true,
            hidePopups: true,
        });
    }

    public ngOnInit(): void {
        if (this.immersiveViewService.isImmersiveViewActive()) {
            this.immersiveViewService.resetImmersiveView();
        }

        this.bearing.set(this.data.map.getBearing());
        this.legendService.isLegendOpen.set(true);
    }

    protected async closeModal(modalResultEnum: ModalResultEnum): Promise<void> {
        this.isMapImageLoading.set(true);

        if (modalResultEnum === ModalResultEnum.CONFIRM) {
            const cleanFileTitle = this.fileTitle().value!.replace(/\..*$/, '');

            await this.mapToImageService.exportImage(
                this.data.map,
                this.bearing(),
                `${cleanFileTitle}.${this.selectedFormat()}`,
                this.selectedFormat(),
                this.selectedPrintSize(),
                {
                    compass: this.addNorthStar(),
                    legend: this.addLegend(),
                    scale: this.addScale(),
                },
            );
        }

        this.isMapImageLoading.set(false);

        this.mapToImageService.isMapExportActive.set(false);
        this.mapToImageService.setControlButtonIcon();
        this.dialogRef.close(modalResultEnum);
    }

    protected selectFormat(format: MapExportFormatEnum[]): void {
        this.selectedFormat.set(format[0]);
    }

    protected selectPrintSize(printSize: MapExportPrintSizeEnum[]): void {
        this.selectedPrintSize.set(printSize[0]);
    }

    protected toggleLegend(): void {
        this.addLegend.set(!this.addLegend());
    }

    protected toggleNorthStar(): void {
        this.addNorthStar.set(!this.addNorthStar());
    }

    protected toggleScale(): void {
        this.addScale.set(!this.addScale());
    }
}
